/**
 * Merges Standard-tab Voluum/Binom actions into the Flow Optimizer drawer
 * footer. Tabs register a list of save actions; the footer renders one Button
 * per entry. Voluum needs 1 (Apply to Voluum); Binom needs up to 3
 * (Save to campaign / Save to rotation / Save to both).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  createContext,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';

import type { FlowApplyFailureNotice } from './tabs/standard/v2/flow-apply';

export interface FlowOptimizerSaveAction {
  id: string;
  label: string;
  /** primary = filled, secondary = outline. */
  variant?: 'primary' | 'secondary';
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  /** Optional help-icon tooltip text rendered next to the button. */
  helpText?: string;
}

export type FlowOptimizerStandardFooterSlot = {
  dirty: boolean;
  applying: boolean;
  onDiscard: () => void;
  actions: FlowOptimizerSaveAction[];
  /** Last Apply failure; cleared on success or discard. */
  applyNotice: FlowApplyFailureNotice | null;
} | null;

/** Standard tab chrome for the main drawer header (refresh, flow name, unsaved). */
export type FlowOptimizerStandardHeaderSlot = {
  flowName: string;
  dirty: boolean;
  syncing: boolean;
  onRefresh: () => void;
} | null;

/**
 * What the drawer HEADER's Refresh button needs, registered from inside the
 * providers (the header is their sibling, so it cannot read those contexts).
 * Absent legs are simply skipped — STANDARD mode has no weights preview, and a
 * dirty Standard draft must not be overwritten by a flow re-read.
 */
export type FlowOptimizerRefreshSlot = {
  /** Re-run the weights preview. Omitted outside CUSTOM mode. */
  refetchWeights?: () => Promise<unknown> | void;
  /** Re-read the tracker flow behind the Standard tab. */
  refetchFlow?: () => Promise<unknown> | void;
  /** True while the Standard draft has unsaved edits — skip the flow re-read. */
  flowDirty?: boolean;
} | null;

type FooterCtx = {
  slot: FlowOptimizerStandardFooterSlot;
  setSlot: Dispatch<SetStateAction<FlowOptimizerStandardFooterSlot>>;
  headerSlot: FlowOptimizerStandardHeaderSlot;
  setHeaderSlot: Dispatch<SetStateAction<FlowOptimizerStandardHeaderSlot>>;
  refreshSlot: FlowOptimizerRefreshSlot;
  setRefreshSlot: Dispatch<SetStateAction<FlowOptimizerRefreshSlot>>;
};

const FlowOptimizerDrawerFooterContext = createContext<FooterCtx | null>(
  null,
);

export function FlowOptimizerDrawerFooterProvider({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const [slot, setSlot] = useState<FlowOptimizerStandardFooterSlot>(null);
  const [headerSlot, setHeaderSlot] =
    useState<FlowOptimizerStandardHeaderSlot>(null);
  const [refreshSlot, setRefreshSlot] = useState<FlowOptimizerRefreshSlot>(null);
  const value = useMemo(
    () => ({
      slot,
      setSlot,
      headerSlot,
      setHeaderSlot,
      refreshSlot,
      setRefreshSlot,
    }),
    [slot, headerSlot, refreshSlot],
  );
  return (
    <FlowOptimizerDrawerFooterContext.Provider value={value}>
      {children}
    </FlowOptimizerDrawerFooterContext.Provider>
  );
}

/** Non-throwing access, for headless chrome that must never break a tab tree
 *  it happens to be mounted in (see RefreshCapabilityRegistrar). */
export function useOptionalFlowOptimizerDrawerFooter(): FooterCtx | null {
  return useContext(FlowOptimizerDrawerFooterContext);
}

export function useFlowOptimizerDrawerFooter(): FooterCtx {
  const c = useContext(FlowOptimizerDrawerFooterContext);
  if (!c) {
    throw new Error('FlowOptimizerDrawerFooterProvider is required');
  }
  return c;
}
